// @system — the honesty line of every legal page: the effective date only when one was set, and a
// "template to review" notice while the business has not reviewed the text (lib/@system/legal.js).
// The notice is readable by the launch checklist: the banner carries data-legal-status and the page
// gets <meta name="legal-status" content="template|reviewed"> in <head>.
import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import { info } from '@/config'
import { effectiveDateLine, isLegalTemplate, legalStatus, contactLink } from '@/app/lib/@system/legal'

const META_NAME = 'legal-status'

/** @param {string} status */
function setLegalMeta(status) {
  if (typeof document === 'undefined') return () => {}
  let tag = document.head.querySelector(`meta[name="${META_NAME}"]`)
  const created = !tag
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute('name', META_NAME)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', status)
  return () => { if (created && tag && tag.parentNode) tag.parentNode.removeChild(tag) }
}

/**
 * @param {{ page: 'terms' | 'privacy' | 'cookies', dateLabel?: string, className?: string }} props
 */
export function LegalNotice({ page, dateLabel = 'Effective', className = '' }) {
  const status = legalStatus(info)
  useEffect(() => setLegalMeta(status), [status])
  const dateLine = effectiveDateLine(info, dateLabel)
  const contact = contactLink(info)
  const template = isLegalTemplate(info)
  return (
    <div className={className}>
      {dateLine ? (
        <p className="text-sm text-brand-text-muted" data-testid={`legal-${page}-date`}>{dateLine}</p>
      ) : null}
      {template ? (
        <div
          role="note"
          data-legal-status={status}
          data-testid={`legal-${page}-template-notice`}
          className="mt-4 flex items-start gap-3 rounded-lg border border-[var(--color-warning)] bg-brand-surface p-4 text-left text-sm text-brand-text"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-warning)]" aria-hidden="true" />
          <p>
            <strong>Template text, to be reviewed.</strong> This page is a general template that {info.name} has
            not reviewed yet. It is not legal advice and may not match how {info.name} actually works. Questions
            go to{' '}
            {contact.isEmail ? (
              <a href={contact.href} className="underline underline-offset-4">{contact.label}</a>
            ) : (
              <Link to={contact.href} className="underline underline-offset-4">{contact.label}</Link>
            )}
            .
          </p>
        </div>
      ) : null}
    </div>
  )
}

/** The support contact as a link: the mailbox when set, else the contact page. @param {{ className?: string, children?: import('react').ReactNode }} props */
export function SupportLink({ className = '', children }) {
  const contact = contactLink(info)
  const label = children ?? contact.label
  return contact.isEmail
    ? <a href={contact.href} className={className}>{label}</a>
    : <Link to={contact.href} className={className}>{label}</Link>
}
